<template>
    <Row
        class="CustomPropertyEquipmentRow"
        :class="{
            'CustomPropertyEquipmentRow--focus': isAnyFieldFocused,
            'CustomPropertyEquipmentRow--readOnly': !editingEnabled,
            'CustomPropertyEquipmentRow--disabled': rbacDisabled
        }"
    >
        <Cell
            v-for="({ name }) in columns"
            :key="name"
            :class="`CustomPropertyEquipmentRow__${name}Cell`"
            :data-test-id="`CustomPropertyEquipmentRow__${name}Cell`"
        >
            <template v-if="name==='action'">
                <div v-if="editingEnabled" class="flex gap-1">
                    <LockInputIcon v-if="rbacDisabled" class="CustomPropertyEquipmentRow__actionButton" />
                    <NewButton
                        v-else
                        variant="flat"
                        size="icon-sm"
                        color="neutral"
                        :disabled="updatingInsured"
                        data-test-id="CustomPropertyEquipmentRow__deleteButton"
                        @click="onClickDelete"
                    >
                        <EidIcon :icon-src="faXmark" alt="delete" />
                    </NewButton>
                </div>
            </template>
            <span
                v-else-if="!editingEnabled"
                :data-test-id="`CustomPropertyEquipmentRow__${name}Value`"
            >{{ readOnlyValue(name) }}</span>
            <JsonSchemaFormElement
                v-else
                v-model="localEquipment[name]"
                :form="fields[name].form"
                :touched="touched"
                :disabled="rbacDisabled"
                :required="form.schema.required.includes(name)"
                :custom-component-input="getCustomComponent"
                @focus="onFocusField(true)"
                @blur="onFocusField(false)"
            />
        </Cell>
    </Row>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import isEqual from 'lodash/isEqual';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { JsonSchemaFormElement } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { Row, Cell } from '@evidentid/dashboard-commons/components/Table';
    import createForm from '@evidentid/json-schema/createForm';
    import { JsonFormProperty } from '@evidentid/json-schema/interfaces/JsonForm';
    import {
        JsonSchemaAbstractObject,
        JsonSchemaArray,
    } from '@evidentid/json-schema/interfaces/JsonSchema';
    import {
        TprmSchemaDisplayFormat,
    } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CustomPropertyEquipment } from '@evidentid/tprm-portal-lib/models/dashboard/CustomProperty.model';

    import NewButton from '@/components/new-button/NewButton.vue';
    import CriterionCurrencyInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/CriterionCurrencyInput.vue';
    import { TableColumn } from '@/modules/entity-details/models/TableColumn.model';
    import { currencyFormatter } from '@/modules/entity-details/utils/currencyFormatter';
    import { formatDateOnly } from '@/modules/entity-events/utils/formatDateOnly';
    import { getJsonSchemaCompFunction } from '@/utils/getJsonSchemaCustomCompFuntion';

    const getCustomComponent = getJsonSchemaCompFunction({
        [TprmSchemaDisplayFormat.currency]: CriterionCurrencyInput,
    });
    export default defineComponent({
        name: 'CustomPropertyEquipmentRow',
        components: {
            JsonSchemaFormElement,
            Cell,
            EidIcon,
            Row,
            LockInputIcon,
            NewButton,
        },
        props: {
            customPropertyEquipment: {
                type: Object as PropType<CustomProperty>,
                required: true,
            },
            equipment: {
                type: Object as PropType<CustomPropertyEquipment>,
                default: (): CustomPropertyEquipment => ({}) as CustomPropertyEquipment,
            },
            updatingInsured: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            columns: {
                type: Array as PropType<TableColumn[]>,
                required: true,
            },
            rbacDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            editingEnabled: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
            touched: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'delete', 'input' ],
        data() {
            const schema = (this.customPropertyEquipment.schema as JsonSchemaArray<JsonSchemaAbstractObject>).items;
            const form = createForm(schema);
            return {
                faXmark,
                form,
                getCustomComponent,
                isAnyFieldFocused: false,
                localEquipment: {} as CustomPropertyEquipment,
            };
        },
        computed: {
            fields(): Record<string, JsonFormProperty> {
                const properties = this.form.getProperties();
                return properties.reduce((acc, property) => {
                    acc[property.name] = property;
                    return acc;
                }, {} as Record<string, JsonFormProperty>);
            },
        },
        watch: {
            equipment: {
                immediate: true,
                handler(newValue?: boolean) {
                    if (newValue) {
                        this.localEquipment = { ...this.equipment };
                    }
                },
            },
            localEquipment: {
                deep: true,
                handler() {
                    // Emit on every field change when in global edit mode.
                    // isEqual guard prevents looping when the parent syncs the prop back.
                    if (this.editingEnabled && !isEqual(this.localEquipment, this.equipment)) {
                        this.$emit('input', { ...this.localEquipment });
                    }
                },
            },
        },
        methods: {
            /**
             * Formats a field for the read-only row.
             *
             * The row renders text rather than inputs outside edit mode because `value-only` is
             * honoured by only one form element — the currency, date and boolean controls all
             * ignore it and stay editable, so relying on it would leave those three writable when
             * the entity is not being edited.
             */
            readOnlyValue(name: string): string {
                const value = (this.equipment as Record<string, unknown>)[name];
                if (value == null || value === '') {
                    return '';
                }
                if (name === 'equipmentValue') {
                    return currencyFormatter().format(Number(value));
                }
                if (name === 'rentalCompleteDate') {
                    return formatDateOnly(String(value));
                }
                if (typeof value === 'boolean') {
                    return value ? 'Yes' : 'No';
                }
                return String(value);
            },
            onFocusField(focused: boolean): void {
                this.isAnyFieldFocused = focused;
            },
            onClickDelete(): void {
                this.$emit('delete');
            },
        },
    });
</script>
